/**
 * Budget category grouping and sort order.
 *
 * Categories are grouped by their plaidPrimary value and displayed under
 * human-readable group headers. Groups are ordered by commonness: everyday
 * spending first, rare/institutional categories last.
 *
 * Within each group, categories with actual spending sort above zero-spend
 * categories (applied dynamically at render time via `sortCategories`).
 */

/** Ordered list of category groups (most common first). */
export const CATEGORY_GROUP_ORDER: readonly string[] = [
  // Household custom categories surface first — the user made them on purpose
  'CUSTOM',
  'FOOD_AND_DRINK',
  'GENERAL_MERCHANDISE',
  'RENT_AND_UTILITIES',
  'TRANSPORTATION',
  'ENTERTAINMENT',
  'PERSONAL_CARE',
  'GENERAL_SERVICES',
  'HOME_IMPROVEMENT',
  'MEDICAL',
  'TRAVEL',
  'LOAN_PAYMENTS',
  'GOVERNMENT_AND_NON_PROFIT',
  'BANK_FEES',
  // Non-budget groups (income, transfers, other) go last when present
  'INCOME',
  'LOAN_DISBURSEMENTS',
  'TRANSFER_IN',
  'TRANSFER_OUT',
  'OTHER',
] as const;

/** Human-readable labels for each plaidPrimary group. */
export const CATEGORY_GROUP_LABELS: Record<string, string> = {
  CUSTOM: 'My Categories',
  FOOD_AND_DRINK: 'Food & Drink',
  GENERAL_MERCHANDISE: 'Shopping',
  RENT_AND_UTILITIES: 'Housing & Utilities',
  TRANSPORTATION: 'Transportation',
  ENTERTAINMENT: 'Entertainment',
  PERSONAL_CARE: 'Personal Care',
  GENERAL_SERVICES: 'Services',
  HOME_IMPROVEMENT: 'Home Improvement',
  MEDICAL: 'Health & Medical',
  TRAVEL: 'Travel',
  LOAN_PAYMENTS: 'Loan Payments',
  GOVERNMENT_AND_NON_PROFIT: 'Taxes & Charity',
  BANK_FEES: 'Bank Fees',
  INCOME: 'Income',
  LOAN_DISBURSEMENTS: 'Loan Disbursements',
  TRANSFER_IN: 'Transfers In',
  TRANSFER_OUT: 'Transfers Out',
  OTHER: 'Other',
};

/** Get the human-readable group label for a plaidPrimary key. */
export function groupLabel(plaidPrimary: string): string {
  return CATEGORY_GROUP_LABELS[plaidPrimary] ?? formatPrimary(plaidPrimary);
}

/** Fallback: convert FOOD_AND_DRINK to "Food & Drink". */
function formatPrimary(key: string): string {
  return key
    .replace(/_AND_/g, ' & ')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Sort index for a plaidPrimary group. Lower = appears first.
 * Unknown groups get a high index to appear at the end.
 */
export function groupSortIndex(plaidPrimary: string): number {
  const idx = CATEGORY_GROUP_ORDER.indexOf(plaidPrimary);
  return idx >= 0 ? idx : CATEGORY_GROUP_ORDER.length;
}

/**
 * Generic category type used by the sort/group utilities.
 * The actual data may have more fields; only these are needed for grouping.
 */
export interface GroupableCategory {
  budgetCategoryId?: number | null;
  categoryName?: string | null;
  plaidPrimary?: string | null;
  /** Monthly actual spend (or monthlyAverage). Used to sort within groups. */
  actual?: number;
}

export interface CategoryGroup<T extends GroupableCategory> {
  label: string;
  plaidPrimary: string;
  categories: T[];
}

/**
 * Group and sort categories according to the standard order.
 *
 * 1. Categories are bucketed by plaidPrimary.
 * 2. Groups are ordered by CATEGORY_GROUP_ORDER (common first).
 * 3. Within each group, categories with spending > 0 appear before
 *    zero-spend categories, preserving the original sub-order otherwise.
 */
export function groupAndSortCategories<T extends GroupableCategory>(
  categories: T[],
): CategoryGroup<T>[] {
  // Bucket by plaidPrimary
  const buckets = new Map<string, T[]>();
  for (const cat of categories) {
    const key = cat.plaidPrimary ?? 'OTHER';
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = [];
      buckets.set(key, bucket);
    }
    bucket.push(cat);
  }

  // Sort groups by defined order
  const sortedKeys = [...buckets.keys()].sort(
    (a, b) => groupSortIndex(a) - groupSortIndex(b),
  );

  return sortedKeys.map((key) => {
    const items = buckets.get(key)!;

    // Within each group: sort by budget amount descending, then spend > 0 first
    items.sort((a, b) => {
      const aBudget = (a as { budgeted?: number }).budgeted ?? 0;
      const bBudget = (b as { budgeted?: number }).budgeted ?? 0;
      if (aBudget !== bBudget) return bBudget - aBudget;
      const aHasSpend = (a.actual ?? 0) > 0 ? 0 : 1;
      const bHasSpend = (b.actual ?? 0) > 0 ? 0 : 1;
      return aHasSpend - bHasSpend;
    });

    return {
      label: groupLabel(key),
      plaidPrimary: key,
      categories: items,
    };
  });
}

/**
 * Build SectionList-compatible sections from raw category records.
 * Used by TransactionList's category picker modal.
 */
export function buildGroupedSections<T extends { id: number; name: string; plaidPrimary: string }>(
  categories: T[],
): { title: string; data: { id: number | null; name: string }[] }[] {
  // Bucket by plaidPrimary
  const buckets = new Map<string, { id: number | null; name: string }[]>();
  for (const c of categories) {
    let bucket = buckets.get(c.plaidPrimary);
    if (!bucket) {
      bucket = [];
      buckets.set(c.plaidPrimary, bucket);
    }
    bucket.push({ id: c.id, name: c.name });
  }

  // Sort groups by defined order
  const sortedKeys = [...buckets.keys()].sort(
    (a, b) => groupSortIndex(a) - groupSortIndex(b),
  );

  const sections: { title: string; data: { id: number | null; name: string }[] }[] = [
    { title: '', data: [{ id: null, name: 'Uncategorized' }] },
  ];
  for (const key of sortedKeys) {
    sections.push({ title: groupLabel(key), data: buckets.get(key)! });
  }
  return sections;
}
